import { cn } from "../lib/utils";

/**
 * v2 Throbber (redesign phase 5, #455). Same file name as v1
 * (`components/ui/throbber.tsx`), so the swap is a move; see
 * docs/design-library/04-implementation-notes.md.
 *
 * Same ring as v1: 8 segments that rotate in 45° steps (`.throbber` in
 * chat.css), so only the highlight moves, at 10 changes per second instead of
 * every frame (~3-8% GPU instead of ~50% for a smooth spinner). Under reduced
 * motion it slows down rather than stopping, so it never looks frozen.
 *
 * v2 (break #70): four sizes (12 / 16 / 20 / 32, was any `h-*`), stroke 2 in
 * the 16-unit grid (was 2.5; 1.5 at 32 so the big one isn't heavy), and a
 * status role:
 * - Standalone it is `role="status"` with a visually hidden label ("Wird
 *   geladen" by default) and `--ink-muted` colour; override with a text class.
 * - `decorative` (inside a Button or IconButton, or next to visible text that
 *   already says it) is a bare aria-hidden SVG in the surrounding text colour,
 *   so it adds nothing to a button's accessible name.
 */

// Leading segment first; each following one sits 45° further back.
const SEGMENT_OPACITIES = [1, 0.875, 0.75, 0.625, 0.5, 0.375, 0.25, 0.15];

export type ThrobberSize = 12 | 16 | 20 | 32;

/** In the 16-unit viewBox: renders 1.5 / 2 / 2.5 / 3 px. */
const STROKE: Record<ThrobberSize, number> = { 12: 2, 16: 2, 20: 2, 32: 1.5 };

export interface ThrobberProps {
  /** 12 next to caption text, 16 next to body text and in controls, 20 in headings, 32 for a loading area. */
  size?: ThrobberSize;
  /** Announced, not shown. German UI text. */
  label?: string;
  /** No role, no label: inside a control, or next to text that says the same. */
  decorative?: boolean;
  className?: string;
}

function Ring({ size, className }: { size: ThrobberSize; className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE[size]}
      strokeDasharray="3.4 34.3"
      aria-hidden="true"
      className={cn("throbber shrink-0", className)}
    >
      {SEGMENT_OPACITIES.map((opacity, i) => (
        <circle key={i} cx={8} cy={8} r={6} opacity={opacity} transform={`rotate(${-106.2 - i * 45} 8 8)`} />
      ))}
    </svg>
  );
}

export function Throbber({ size = 16, label = "Wird geladen", decorative = false, className }: ThrobberProps) {
  if (decorative) return <Ring size={size} className={className} />;
  return (
    <span role="status" className={cn("inline-flex shrink-0 text-ink-muted", className)}>
      <Ring size={size} />
      <span className="sr-only">{label}</span>
    </span>
  );
}
